import { useQuery } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { orgFetch } from '@/lib/org-api'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface PipelineRow {
  id: string
  artistId: string
  artistName: string
  stage: string
  contactName: string | null
  contactOrg: string | null
  eventDate: string | null
  eventCity: string | null
  budgetMaxMinor: number | null
  createdAt: string
}

export function RosterPipelinePage() {
  const user = useAuthStore((s) => s.user)
  const { data, error, isLoading } = useQuery<PipelineRow[], ApiClientError>({
    queryKey: ['org-pipeline'],
    queryFn: () => orgFetch<PipelineRow[]>('/pipeline'),
    enabled: !!user?.organisationId,
  })

  if (!user?.organisationId) {
    return (
      <div>
        <PageHeader title="Roster pipeline" />
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
          You're not part of an organisation.
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Roster pipeline"
        subtitle="Every inquiry across every artist in your organisation."
      />
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No inquiries across the roster yet."
          columns={[
            { key: 'artist', header: 'Artist', render: (r) => r.artistName },
            {
              key: 'stage',
              header: 'Stage',
              render: (r) => (
                <span className="capitalize">{r.stage.replace(/_/g, ' ')}</span>
              ),
            },
            { key: 'contact', header: 'Contact', render: (r) => r.contactName ?? '—' },
            { key: 'org', header: 'From', render: (r) => r.contactOrg ?? '—' },
            { key: 'city', header: 'City', render: (r) => r.eventCity ?? '—' },
            {
              key: 'date',
              header: 'Date',
              render: (r) =>
                r.eventDate ? new Date(r.eventDate).toLocaleDateString() : '—',
            },
            {
              key: 'budget',
              header: 'Budget',
              align: 'right',
              render: (r) =>
                r.budgetMaxMinor != null
                  ? `£${(r.budgetMaxMinor / 100).toLocaleString()}`
                  : '—',
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}
